import axios from "axios";
import { getUsers } from "./getUser";

/* FETCHING USER SIGN TYPE REQUEST */
const loginType = localStorage.getItem("loginType");

/* TOKEN REFRESH STATE */
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

const refreshAccessToken = async () => {
  const user = getUsers();
  if (!user?.refresh_token) throw new Error("No refresh token");
  const AUTH_URL = process.env.REACT_APP_AUTH_BASE_URL;
  let response;
  try {
    response = await axios.post(`${AUTH_URL}/v1/auth/refresh`, {
      refresh_token: user.refresh_token,
    });
  } catch (error) {
    // Another tab may have used the same refresh token a moment earlier and saved the new pair
    const latestUser = getUsers();
    if (latestUser?.refresh_token && latestUser.refresh_token !== user.refresh_token) {
      return latestUser.access_token;
    }
    throw error;
  }
  const { access_token, refresh_token } = response.data;
  user.access_token = access_token;
  user.refresh_token = refresh_token;
  const currentLoginType = localStorage.getItem("loginType");
  localStorage.setItem("user", JSON.stringify(user));
  if (currentLoginType === "false") {
    sessionStorage.setItem("user", JSON.stringify(user));
  }
  return access_token;
};

export const logoutFromServer = async () => {
  try {
    const user = getUsers();
    if (user?.access_token) {
      const AUTH_URL = process.env.REACT_APP_AUTH_BASE_URL;
      await axios.post(`${AUTH_URL}/v1/auth/logout`, null, {
        headers: { Authorization: `Bearer ${user.access_token}` },
      });
    }
  } catch {
    // best-effort: server-side token cleanup is not critical for client logout
  }
};

const addResponseInterceptor = (instance) => {
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      if (error.response?.status === 401 && !originalRequest._retry) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return instance(originalRequest);
          });
        }
        originalRequest._retry = true;
        isRefreshing = true;
        try {
          const newToken = await refreshAccessToken();
          processQueue(null, newToken);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return instance(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          localStorage.clear();
          sessionStorage.clear();
          window.location.replace("/");
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }
      return Promise.reject(error);
    }
  );
};

// const isProd = process.env.REACT_APP_IS_PROD === "true";
// const isLocal = process.env.REACT_APP_IS_LOCAL === "true";

// console.log(typeof isProd + " " + isProd);
// console.log(typeof isLocal + " " + isLocal);

/* AUTHENTICATION */
// const LOCAL_AUTH_URL = "http://localhost:4000";
// const AUTH_QA_URL = "https://auth-qa.rs-chain.com";
export const AUTH_BASE_URL = process.env.REACT_APP_AUTH_BASE_URL;
// const AUTH_PROD_URL = "https://auth.rs-chain.com";

/* ORDER */
// const ORDER_LOCAL_URL = "http://localhost:9000";
// const ORDER_QA_URL = "https://order-qa.rs-chain.com";
export const ORDER_BASE_URL = process.env.REACT_APP_ORD_BASE_URL;
// const ORDER_PROD_URL = "https://order.rs-chain.com";

/* ANALYTICS */
// const ANALYTICS_LOCAL_URL = "http://localhost:8000";
// const ANALYTICS_QA_URL = "https://payout-qa.rs-chain.com";
export const ANALYTICS_BASE_URL = process.env.REACT_APP_ANALYTICS_BASE_URL;
// const ANALYTICS_PROD_URL = "https://analytics.rs-chain.com";

/* SHIPMENT */
// const SHIPMENT_LOCAL_URL = "http://localhost:3500";
// const SHIPMENT_QA_URL = "https://shipment-qa.rs-chain.com";
export const SHIPMENT_BASE_URL = process.env.REACT_APP_SHIPMENT_BASE_URL;
// const SHIPMENT_PROD_URL = "https://shipment.rs-chain.com";

/* PAYMENTS */
// const PAYMENTS_LOCAL_URL = "http://localhost:7500";
// const PAYMENTS_QA_URL = "https://payout-qa.rs-chain.com";
export const PAYMENT_BASE_URL = process.env.REACT_APP_PAYMENTS_BASE_URL;
// const PAYMENTS_PROD_URL = "https://payout.rs-chain.com";
// const getEnvironmentURL = (localURL, qaURL, prodURL) => {
//   if (isLocal) return localURL;
//   return isProd ? prodURL : qaURL;
// };

// export const AUTH_BASE_URL = getEnvironmentURL(
//   LOCAL_AUTH_URL,
//   AUTH_QA_URL,
//   AUTH_PROD_URL
// );
// export const ORDER_BASE_URL = getEnvironmentURL(
//   ORDER_LOCAL_URL,
//   ORDER_QA_URL,
//   ORDER_PROD_URL
// );
// export const ANALYTICS_BASE_URL = getEnvironmentURL(
//   ANALYTICS_LOCAL_URL,
//   ANALYTICS_QA_URL,
//   ANALYTICS_PROD_URL
// );
// export const SHIPMENT_BASE_URL = getEnvironmentURL(
//   SHIPMENT_LOCAL_URL,
//   SHIPMENT_QA_URL,
//   SHIPMENT_PROD_URL
// );
// export const PAYMENT_BASE_URL = getEnvironmentURL(
//   PAYMENTS_LOCAL_URL,
//   PAYMENTS_QA_URL,
//   PAYMENTS_PROD_URL
// );

const defaultHeader = () => {
  const access_token =
    loginType === "false"
      ? JSON.parse(sessionStorage.getItem("user"))?.access_token
      : JSON.parse(localStorage.getItem("user"))?.access_token;
  return {
    Authorization: `Bearer ${access_token}`,
    "Content-Type": "application/json",
    "Access-Control-Allow-Origin": "*",
    "Access-Control-Allow-Methods": "DELETE, POST, GET, OPTIONS",
    "Access-Control-Allow-Headers":
      "Content-Type, Access-Control-Allow-Headers, Authorization, X-Requested-With",
    Accept: "application/json",
  };
};

export const authenticationApiRequest = async (
  method,
  endpoint,
  data = null,
  params,
  headers = {},
  _retried = false
) => {
  try {
    const url = `${AUTH_BASE_URL}/${endpoint}`;
    console.log(url);
    const config = {
      method,
      url,
      params,
      data,
      headers: Object.keys(headers)?.length ? headers : defaultHeader(),
    };

    const response = await axios(config);
    return response.data;
  } catch (error) {
    if (error.response && error.response.status === 401 && !_retried) {
      try {
        await refreshAccessToken();
        return authenticationApiRequest(method, endpoint, data, params, headers, true);
      } catch {
        localStorage.clear();
        sessionStorage.clear();
        window.location.replace("/");
      }
    } else {
      throw new Error(error.response?.data?.detail || error.message);
    }
  }
};

export const analyticsApiRequest = async (
  method,
  endpoint,
  data = null,
  params,
  headers = {},
  _retried = false
) => {
  try {
    const url = `${ANALYTICS_BASE_URL}/${endpoint}`;
    console.log(url);
    const config = {
      method,
      url,
      params,
      data,
      headers: Object.keys(headers)?.length ? headers : defaultHeader(),
    };

    const response = await axios(config);
    return response.data;
  } catch (error) {
    if (error.response && error.response.status === 401 && !_retried) {
      try {
        await refreshAccessToken();
        return analyticsApiRequest(method, endpoint, data, params, headers, true);
      } catch {
        localStorage.clear();
        sessionStorage.clear();
        window.location.replace("/");
      }
    } else {
      throw new Error(error.response?.data?.detail || error.message);
    }
  }
};

export const orderManagementApiRequest = async (
  method,
  endpoint,
  data = null,
  params,
  headers = {},
  _retried = false
) => {
  try {
    const url = `${ORDER_BASE_URL}/${endpoint}`;
    console.log(url);
    const config = {
      method,
      url,
      params,
      data,
      headers: Object.keys(headers)?.length ? headers : defaultHeader(),
    };

    const response = await axios(config);
    return response.data;
  } catch (error) {
    if (error.response && error.response.status === 401 && !_retried) {
      try {
        await refreshAccessToken();
        return orderManagementApiRequest(method, endpoint, data, params, headers, true);
      } catch {
        localStorage.clear();
        sessionStorage.clear();
        window.location.replace("/");
      }
    } else {
      throw new Error(error.response?.data?.detail || error.message);
    }
  }
};

export const shipmentApiRequest = async (
  method,
  endpoint,
  data = null,
  params,
  headers = {},
  _retried = false
) => {
  try {
    const url = `${SHIPMENT_BASE_URL}/${endpoint}`;
    console.log(url);
    const config = {
      method,
      url,
      params,
      data,
      headers: Object.keys(headers)?.length ? headers : defaultHeader(),
    };

    const response = await axios(config);
    return response.data;
  } catch (error) {
    if (error.response && error.response.status === 401 && !_retried) {
      try {
        await refreshAccessToken();
        return shipmentApiRequest(method, endpoint, data, params, headers, true);
      } catch {
        localStorage.clear();
        sessionStorage.clear();
        window.location.replace("/");
      }
    } else {
      throw new Error(error.response?.data?.detail || error.message);
    }
  }
};

export const payoutsApiRequest = async (
  method,
  endpoint,
  data = null,
  params,
  headers = {},
  _retried = false
) => {
  try {
    const url = `${PAYMENT_BASE_URL}/${endpoint}`;
    console.log(url);
    const config = {
      method,
      url,
      params,
      data,
      headers: Object.keys(headers)?.length ? headers : defaultHeader(),
    };

    const response = await axios(config);
    return response.data;
  } catch (error) {
    if (error.response && error.response.status === 401 && !_retried) {
      try {
        await refreshAccessToken();
        return payoutsApiRequest(method, endpoint, data, params, headers, true);
      } catch {
        localStorage.clear();
        sessionStorage.clear();
        window.location.replace("/");
      }
    } else {
      throw new Error(error.response?.data?.detail || error.message);
    }
  }
};

export const createAxiosInstanceUser = (endpoint) => {
  const baseUrl = `${AUTH_BASE_URL}`;
  console.log(baseUrl);
  const instance = axios.create({
    baseURL: `${AUTH_BASE_URL}`,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });

  instance.interceptors.request.use(
    async (config) => {
      try {
        const user = getUsers();
        if (user) {
          const token = user?.access_token;
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          } else {
            console.warn("No access token found in user data.");
          }
        }
      } catch (error) {
        console.error("Error retrieving user data:", error.message);
      }
      return config;
    },
    (error) => {
      console.error(error);
    }
  );

  addResponseInterceptor(instance);
  return instance;
};

export const createAxiosInstanceAanlysis = (endpoint) => {
  const instance = axios.create({
    baseURL: `${ANALYTICS_BASE_URL}`,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });

  instance.interceptors.request.use(
    async (config) => {
      try {
        const user = getUsers();
        if (user) {
          const token = user?.access_token;
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          } else {
            console.warn("No access token found in user data.");
          }
        }
      } catch (error) {
        console.error("Error retrieving user data:", error.message);
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  addResponseInterceptor(instance);
  return instance;
};

export const createAxiosInstanceOrder = (endpoint) => {
  const instance = axios.create({
    baseURL: `${ORDER_BASE_URL}`,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });

  instance.interceptors.request.use(
    async (config) => {
      try {
        const user = getUsers();
        if (user) {
          const token = user?.access_token;
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          } else {
            console.warn("No access token found in user data.");
          }
        }
      } catch (error) {
        console.error("Error retrieving user data:", error.message);
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  addResponseInterceptor(instance);
  return instance;
};

export const createAxiosShipment = (endpoint) => {
  const instance = axios.create({
    baseURL: `${SHIPMENT_BASE_URL}`,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });

  instance.interceptors.request.use(
    async (config) => {
      try {
        const user = getUsers();
        if (user) {
          const token = user?.access_token;
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          } else {
            console.warn("No access token found in user data.");
          }
        }
      } catch (error) {
        console.error("Error retrieving user data:", error.message);
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  addResponseInterceptor(instance);
  return instance;
};

export const createAxiosPayment = (endpoint) => {
  const instance = axios.create({
    baseURL: `${PAYMENT_BASE_URL}`,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });

  instance.interceptors.request.use(
    async (config) => {
      try {
        const user = getUsers();
        if (user) {
          const token = user?.access_token;
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          } else {
            console.warn("No access token found in user data.");
          }
        }
      } catch (error) {
        console.error("Error retrieving user data:", error.message);
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  addResponseInterceptor(instance);
  return instance;
};
